import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DEPARTMENTS } from '../utils/departmentUtils';
import { apiClient } from '../services/api';

interface CreateProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const CreateProjectModal = ({ isOpen, onClose, onSuccess }: CreateProjectModalProps) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        status: 'active',
        dueDate: '',
        department: 'GEN'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!formData.name.trim()) {
            setError('Project name is required');
            return;
        }

        setIsSubmitting(true);

        try {
            await apiClient.createProject({
                name: formData.name,
                description: formData.description,
                status: formData.status,
                dueDate: formData.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // Default 30 days from now
                progress: 0,
                metadata: {
                    department: formData.department
                }
            });

            // Reset form
            setFormData({
                name: '',
                description: '',
                status: 'active',
                dueDate: '',
                department: 'General'
            });

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Failed to create project:', err);
            // Handle validation errors specifically
            const errorMessage = err.message === 'Validation failed'
                ? 'Please check your input. Project name is required.'
                : (err.message || 'Failed to create project. Please try again.');
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData({
                name: '',
                description: '',
                status: 'active',
                dueDate: '',
                department: 'General'
            });
            setError('');
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="relative w-full max-w-lg bg-gradient-to-br from-premium-wood-dark to-black border-2 border-premium-gold/30 rounded-2xl shadow-2xl overflow-hidden"
                    >
                        {/* Header with Wood Texture */}
                        <div className="relative bg-black/40 border-b border-premium-gold/20 p-6">
                            <div
                                className="absolute inset-0 opacity-20 mix-blend-overlay"
                                style={{
                                    backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=2670&auto=format&fit=crop')`,
                                    backgroundSize: 'cover'
                                }}
                            />
                            <h2 className="relative text-2xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold-light to-premium-gold">
                                Create New Project
                            </h2>
                            <p className="relative text-white/60 text-sm mt-1">Start a new venture or collaboration</p>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Project Name */}
                            <div>
                                <label className="block text-premium-gold text-sm font-medium mb-2">
                                    Project Name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter project name"
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-premium-gold/50 transition-colors"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-premium-gold text-sm font-medium mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Describe your project (optional)"
                                    rows={3}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-premium-gold/50 transition-colors resize-none"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* Department Selection */}
                            <div>
                                <label className="block text-premium-gold text-sm font-medium mb-2">
                                    Department / Industry
                                </label>
                                <select
                                    value={formData.department}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, department: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-premium-gold/50 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    {Object.entries(DEPARTMENTS).map(([code, label]) => (
                                        <option key={code} value={code} className="bg-slate-900">
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Status and Due Date in a row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-premium-gold text-sm font-medium mb-2">
                                        Status
                                    </label>
                                    <select
                                        value={formData.status}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, status: e.target.value })}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-premium-gold/50 transition-colors"
                                        disabled={isSubmitting}
                                    >
                                        <option value="active">Active</option>
                                        <option value="planning">Planning</option>
                                        <option value="completed">Completed</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-premium-gold text-sm font-medium mb-2">
                                        Due Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.dueDate}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, dueDate: e.target.value })}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-premium-gold/50 transition-colors"
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className="flex-1 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Creating...' : 'Create Project'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default CreateProjectModal;
